import { useCallback, useState } from "react";
import { useSessionToken } from "./api/context";
import { isSetPasswordRoute } from "./auth/set-password";
import { SetPasswordPage } from "./auth/SetPasswordPage";
import { SignInFlow } from "./auth/SignInFlow";
import { t } from "./i18n";
import { useLanguage } from "./i18n/session-language";
import { useHash } from "./navigation/use-hash";
import { AlertsProvider, AlertStack } from "./shell/alerts";
import { Shell } from "./shell/Shell";

/**
 * `#/set-password` is public and outside the shell: an invited staff member
 * has no session yet. Otherwise, no session: sign in. A session: the shell.
 * Any request answering `unauthenticated` or `session_expired` clears the
 * session, which lands here and returns to sign-in.
 */
export function App() {
  const token = useSessionToken();
  // Sign-in and set-password follow the language too (this session's pick, else French).
  useLanguage();
  const hash = useHash();
  const [signOutNotice, setSignOutNotice] = useState<unknown>(null);
  const [info, setInfo] = useState<string | null>(null);
  const onSignedOut = useCallback((notice: unknown) => setSignOutNotice(notice), []);
  const onPasswordSet = useCallback(() => {
    setInfo(t("setPassword.done"));
    window.location.hash = "";
  }, []);
  return (
    <AlertsProvider>
      <AlertStack />
      <Body
        hash={hash}
        token={token}
        notice={signOutNotice}
        info={info}
        onSignedOut={onSignedOut}
        onPasswordSet={onPasswordSet}
      />
    </AlertsProvider>
  );
}

function Body(props: {
  hash: string;
  token: string | null;
  notice: unknown;
  info: string | null;
  onSignedOut: (notice: unknown) => void;
  onPasswordSet: () => void;
}) {
  if (isSetPasswordRoute(props.hash)) return <SetPasswordPage onDone={props.onPasswordSet} />;
  if (!props.token) return <SignInFlow notice={props.notice} info={props.info} />;
  return <Shell onSignedOut={props.onSignedOut} />;
}
